import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Plus, Calendar, User, Trash2, Edit2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

// Add custom pulse animation for background only
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse-bg {
    0%, 100% {
      background-color: rgb(254 226 226);
    }
    50% {
      background-color: rgb(254 202 202);
    }
  }
`;
if (!document.head.querySelector('style[data-pulse-bg]')) {
  style.setAttribute('data-pulse-bg', 'true');
  document.head.appendChild(style);
}

interface DayOffRecord {
  id: string;
  driver_id: string;
  driver_name: string;
  start_date: string;
  end_date: string;
  hire_date: string;
  month: string;
  number_of_days: number;
  driver_return: boolean;
  return_date: string | null;
  note: string | null;
  created_at: string;
}

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
  hire_date: string;
}

const MONTHS = [
  { value: 'January', color: 'bg-blue-500 text-white' },
  { value: 'February', color: 'bg-purple-500 text-white' },
  { value: 'March', color: 'bg-green-500 text-white' },
  { value: 'April', color: 'bg-yellow-500 text-white' },
  { value: 'May', color: 'bg-pink-500 text-white' },
  { value: 'June', color: 'bg-indigo-500 text-white' },
  { value: 'July', color: 'bg-red-500 text-white' },
  { value: 'August', color: 'bg-orange-500 text-white' },
  { value: 'September', color: 'bg-teal-500 text-white' },
  { value: 'October', color: 'bg-amber-500 text-white' },
  { value: 'November', color: 'bg-cyan-500 text-white' },
  { value: 'December', color: 'bg-emerald-500 text-white' },
];

export function DayOffView() {
  const toast = useToast();
  const [dayOffRecords, setDayOffRecords] = useState<DayOffRecord[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Inline editing states
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');
  const [editingMonth, setEditingMonth] = useState<string | null>(null);
  const [showReturnDateModal, setShowReturnDateModal] = useState(false);
  const [selectedRecordForReturn, setSelectedRecordForReturn] = useState<DayOffRecord | null>(null);
  const [tempReturnDate, setTempReturnDate] = useState('');
  
  // Edit/Delete states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<DayOffRecord | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState<DayOffRecord | null>(null);
  const [showDeleteDriverModal, setShowDeleteDriverModal] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<string | null>(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editMonth, setEditMonth] = useState('');
  const [editDriverReturn, setEditDriverReturn] = useState(false);
  const [editReturnDate, setEditReturnDate] = useState('');
  const [editNote, setEditNote] = useState('');
  
  // Form state
  const [selectedDriver, setSelectedDriver] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [month, setMonth] = useState('');
  const [driverReturn, setDriverReturn] = useState(false);
  const [returnDate, setReturnDate] = useState('');
  const [note, setNote] = useState('');

  // Calculate number of days automatically
  const calculateDays = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const diffTime = Math.abs(endDateObj.getTime() - startDateObj.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    return diffDays;
  };

  const numberOfDays = calculateDays(startDate, endDate);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load drivers (all except terminated)
      const { data: driversData, error: driversError } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, hire_date')
        .not('driver_status', 'eq', 'terminated')
        .order('last_name');

      if (driversError) throw driversError;
      setDrivers(driversData || []);

      // Load day off records
      const { data: recordsData, error: recordsError } = await supabase
        .from('driver_day_off')
        .select('*')
        .order('start_date', { ascending: false });

      if (recordsError) throw recordsError;
      setDayOffRecords(recordsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load day off records');
    } finally {
      setLoading(false);
    }
  };

  const toggleDriver = (driverName: string) => {
    const newExpanded = new Set(expandedDrivers);
    if (newExpanded.has(driverName)) {
      newExpanded.delete(driverName);
    } else {
      newExpanded.add(driverName);
    }
    setExpandedDrivers(newExpanded);
  };

  const handleAddDayOff = async () => {
    if (!selectedDriver || !startDate || !endDate || !month) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (numberOfDays <= 0) {
      toast.error('End date must be after start date');
      return;
    }

    try {
      const driver = drivers.find(d => d.id === selectedDriver);
      if (!driver) return;

      const { data: newDayOff, error } = await supabase
        .from('driver_day_off')
        .insert({
          driver_id: selectedDriver,
          driver_name: `${driver.first_name} ${driver.last_name}`.toUpperCase(),
          start_date: startDate,
          end_date: endDate,
          hire_date: driver.hire_date,
          month,
          number_of_days: numberOfDays,
          driver_return: driverReturn,
          return_date: driverReturn ? returnDate : null,
          note: note || null,
        })
        .select('id')
        .single();

      if (error) throw error;

      if (newDayOff) {
        const { emailReminders } = await import('../../services/emailReminders');
        void emailReminders.dayOffRequested({
          id: newDayOff.id,
          driver_name: `${driver.first_name} ${driver.last_name}`,
          start_date: startDate, end_date: endDate, number_of_days: numberOfDays,
        });
      }

      toast.success('Day off record added successfully');
      setShowAddModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error adding day off:', error);
      toast.error('Failed to add day off record');
    }
  };

  const resetForm = () => {
    setSelectedDriver('');
    setDriverSearch('');
    setShowDriverDropdown(false);
    setStartDate('');
    setEndDate('');
    setMonth('');
    setDriverReturn(false);
    setReturnDate('');
    setNote('');
  };

  const handleDriverSelect = (driver: Driver) => {
    setSelectedDriver(driver.id);
    setDriverSearch(`${driver.first_name} ${driver.last_name}`);
    setShowDriverDropdown(false);
  };

  const filteredDrivers = drivers.filter(driver => {
    const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
    return fullName.includes(driverSearch.toLowerCase());
  });

  // Filter day off records by search query
  const filteredRecords = dayOffRecords.filter(record => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      record.driver_name.toLowerCase().includes(query) ||
      record.month.toLowerCase().includes(query) ||
      record.note?.toLowerCase().includes(query)
    );
  });

  const handleToggleDriverReturn = async (record: DayOffRecord) => {
    if (!record.driver_return) {
      // Changing from No to Yes - show return date modal
      setSelectedRecordForReturn(record);
      setTempReturnDate('');
      setShowReturnDateModal(true);
    } else {
      // Changing from Yes to No - just update
      try {
        const { error } = await supabase
          .from('driver_day_off')
          .update({ 
            driver_return: false,
            return_date: null 
          })
          .eq('id', record.id);

        if (error) throw error;
        toast.success('Driver return status updated');
        loadData();
      } catch (error) {
        console.error('Error updating driver return:', error);
        toast.error('Failed to update driver return status');
      }
    }
  };

  const handleConfirmReturnDate = async () => {
    if (!selectedRecordForReturn || !tempReturnDate) {
      toast.error('Please select a return date');
      return;
    }

    try {
      // Recalculate number of days based on actual return date
      const actualDays = calculateDays(selectedRecordForReturn.start_date, tempReturnDate);
      
      const { error } = await supabase
        .from('driver_day_off')
        .update({ 
          driver_return: true,
          return_date: tempReturnDate,
          number_of_days: actualDays
        })
        .eq('id', selectedRecordForReturn.id);

      if (error) throw error;
      
      // Show message if days changed
      const originalDays = selectedRecordForReturn.number_of_days;
      if (actualDays !== originalDays) {
        if (actualDays < originalDays) {
          toast.success(`Driver returned early! Days updated from ${originalDays} to ${actualDays}`);
        } else {
          toast.success(`Vacation extended! Days updated from ${originalDays} to ${actualDays}`);
        }
      } else {
        toast.success('Driver return status updated');
      }
      
      setShowReturnDateModal(false);
      setSelectedRecordForReturn(null);
      setTempReturnDate('');
      loadData();
    } catch (error) {
      console.error('Error updating driver return:', error);
      toast.error('Failed to update driver return status');
    }
  };

  const handleUpdateNote = async (recordId: string, newNote: string) => {
    try {
      const { error } = await supabase
        .from('driver_day_off')
        .update({ note: newNote || null })
        .eq('id', recordId);

      if (error) throw error;
      toast.success('Note updated');
      setEditingNote(null);
      setEditingNoteValue('');
      loadData();
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to update note');
    }
  };

  const startEditingNote = (record: DayOffRecord) => {
    setEditingNote(record.id);
    setEditingNoteValue(record.note || '');
  };

  const handleUpdateMonth = async (recordId: string, newMonth: string) => {
    try {
      const { error } = await supabase
        .from('driver_day_off')
        .update({ month: newMonth })
        .eq('id', recordId);

      if (error) throw error;

      toast.success('Month updated');
      setEditingMonth(null);
      loadData();
    } catch (error) {
      console.error('Error updating month:', error);
      toast.error('Failed to update month');
    }
  };

  const handleDeleteRecord = async () => {
    if (!recordToDelete) return;

    try {
      const { error } = await supabase
        .from('driver_day_off')
        .delete()
        .eq('id', recordToDelete.id);

      if (error) throw error;

      toast.success('Vacation record deleted');
      setShowDeleteModal(false);
      setRecordToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting record:', error);
      toast.error('Failed to delete vacation record');
    }
  };

  const openEditModal = (record: DayOffRecord) => {
    setRecordToEdit(record);
    setEditStartDate(record.start_date);
    setEditEndDate(record.end_date);
    setEditMonth(record.month);
    setEditDriverReturn(record.driver_return);
    setEditReturnDate(record.return_date || '');
    setEditNote(record.note || '');
    setShowEditModal(true);
  };

  const handleEditRecord = async () => {
    if (!recordToEdit || !editStartDate || !editEndDate || !editMonth) {
      toast.error('Please fill in all required fields');
      return;
    }

    const actualDays = calculateDays(editStartDate, editEndDate);

    try {
      const { error } = await supabase
        .from('driver_day_off')
        .update({
          start_date: editStartDate,
          end_date: editEndDate,
          month: editMonth,
          number_of_days: actualDays,
          driver_return: editDriverReturn,
          return_date: editDriverReturn ? editReturnDate : null,
          note: editNote || null,
        })
        .eq('id', recordToEdit.id);

      if (error) throw error;

      toast.success('Vacation record updated');
      setShowEditModal(false);
      setRecordToEdit(null);
      loadData();
    } catch (error) {
      console.error('Error updating record:', error);
      toast.error('Failed to update vacation record');
    }
  };

  const handleDeleteDriver = async () => {
    if (!driverToDelete) return;

    try {
      // Delete all vacation records for this driver
      const { error } = await supabase
        .from('driver_day_off')
        .delete()
        .eq('driver_name', driverToDelete);

      if (error) throw error;

      toast.success(`All vacation records for ${driverToDelete} have been deleted`);
      setShowDeleteDriverModal(false);
      setDriverToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting driver records:', error);
      toast.error('Failed to delete driver records');
    }
  };

  const getMonthColor = (monthValue: string) => {
    return MONTHS.find(m => m.value === monthValue)?.color || 'bg-gray-100 text-gray-700';
  };

  // Format date consistently in UTC to avoid timezone issues
  const formatDateUTC = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00Z'); // Force UTC interpretation
    return date.toLocaleDateString('en-US', { 
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getRowColor = (record: DayOffRecord) => {
    if (!record.driver_return) {
      return 'hover:bg-white'; // No color if not returned yet
    }

    // Calculate planned days (start_date to end_date)
    const plannedDays = calculateDays(record.start_date, record.end_date);
    const actualDays = record.number_of_days;

    if (actualDays <= plannedDays) {
      // Returned on time or early - green
      return 'bg-green-50 hover:bg-green-100';
    } else {
      // Extended stay - red
      return 'bg-red-50 hover:bg-red-100';
    }
  };

  // Group records by driver
  const groupedRecords = dayOffRecords.reduce((acc, record) => {
    if (!acc[record.driver_name]) {
      acc[record.driver_name] = [];
    }
    acc[record.driver_name].push(record);
    return acc;
  }, {} as Record<string, DayOffRecord[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Day Off</h1>
          <p className="text-slate-600 mt-1">Manage driver vacation and time off records</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Day Off
        </button>
      </div>

      {/* Color Legend */}
      <div className="mb-4 bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-6 flex-wrap">
          <span className="text-sm font-semibold text-slate-700">Legend:</span>
          
          {/* Row Colors */}
          <div className="flex items-center gap-2">
            <div className="w-16 h-6 bg-green-50 border border-green-200 rounded"></div>
            <span className="text-xs text-slate-600">On time/Early return</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-16 h-6 bg-red-50 border border-red-200 rounded"></div>
            <span className="text-xs text-slate-600">Extended vacation</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-16 h-6 bg-white border border-slate-200 rounded"></div>
            <span className="text-xs text-slate-600">Not returned</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <input
            type="text"
            placeholder="Search by driver name, month, or note..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <tbody className="divide-y divide-slate-200">
              {Object.entries(groupedRecords).filter(([driverName, records]) => {
                if (!searchQuery) return true;
                const query = searchQuery.toLowerCase();
                // Search in driver name, or any of their vacation records' months or notes
                return driverName.toLowerCase().includes(query) ||
                  records.some(r => 
                    r.month.toLowerCase().includes(query) ||
                    r.note?.toLowerCase().includes(query)
                  );
              }).map(([driverName, records]) => {
                const isExpanded = expandedDrivers.has(driverName);
                
                // Calculate days off in last 30 days
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                
                const recordsInLast30 = records.filter(r => new Date(r.start_date) >= thirtyDaysAgo);
                const daysInLast30 = recordsInLast30.reduce((sum, r) => sum + r.number_of_days, 0);
                const tripsInLast30 = recordsInLast30.length;
                
                const totalDays = records.reduce((sum, r) => sum + r.number_of_days, 0);

                // Check if driver has 2+ vacations in the same month
                const monthCounts = records.reduce((acc, r) => {
                  acc[r.month] = (acc[r.month] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>);
                const hasMultipleVacationsInMonth = Object.values(monthCounts).some(count => count >= 2);

                return (
                  <>
                    <tr>
                      <td colSpan={5} className="p-0">
                        <div 
                          onClick={() => toggleDriver(driverName)}
                          className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-all duration-200 border-l-4 ${
                            hasMultipleVacationsInMonth
                              ? 'bg-gradient-to-r from-red-100 to-red-200 border-red-500 hover:from-red-200 hover:to-red-300'
                              : 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-500 hover:from-blue-100 hover:to-blue-200'
                          }`}
                          style={hasMultipleVacationsInMonth ? {
                            animation: 'pulse-bg 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                          } : undefined}
                        >
                          <div className="flex items-center gap-3">
                            <div className="transform transition-transform duration-200">
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-slate-600" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-slate-600" />
                              )}
                            </div>
                            <User className="w-5 h-5 text-blue-600" />
                            <div>
                              <h3 className="font-bold text-base text-slate-900">{driverName}</h3>
                              <p className="text-xs text-slate-600">
                                {daysInLast30} days (30d) • {tripsInLast30} {tripsInLast30 === 1 ? 'trip' : 'trips'} • {totalDays} total days
                              </p>
                              {hasMultipleVacationsInMonth && (
                                <p className="text-xs font-bold text-red-700 mt-1">
                                  ⚠️ Alert: Driver took 2+ vacations in {Object.entries(monthCounts)
                                    .filter(([_, count]) => count >= 2)
                                    .map(([month]) => month)
                                    .join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {(() => {
                              // Driver is on vacation if they have any record where driver_return is false
                              const isCurrentlyOnVacation = records.some(r => !r.driver_return);
                              
                              return isCurrentlyOnVacation ? (
                                <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-yellow-500 text-white">
                                  ON VACATION
                                </span>
                              ) : (
                                <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-green-500 text-white">
                                  ACTIVE
                                </span>
                              );
                            })()}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDriverToDelete(driverName);
                                setShowDeleteDriverModal(true);
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete all records for this driver"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={5} className="px-4 py-0">
                          <div className="bg-slate-50 p-4">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                  <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">From - To</th>
                                  <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-28">Hire Date</th>
                                  <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-28">Month</th>
                                  <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-20">Days</th>
                                  <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-24">Return</th>
                                  <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-28">Return Date</th>
                                  <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Note</th>
                                  <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-24">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {records.filter(record => {
                                  if (!searchQuery) return true;
                                  const query = searchQuery.toLowerCase();
                                  return (
                                    record.month.toLowerCase().includes(query) ||
                                    record.note?.toLowerCase().includes(query)
                                  );
                                }).map((record) => (
                                  <tr key={record.id} className={`border-b border-slate-200 hover:bg-slate-50/50 transition-all ${getRowColor(record)}`}>
                                    <td className="px-3 py-2 min-w-[180px] cursor-pointer hover:bg-slate-100 transition-colors">
                                      <span className="text-xs text-slate-700 font-medium">
                                        {formatDateUTC(record.start_date)} - {formatDateUTC(record.end_date)}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 min-w-[100px] cursor-pointer hover:bg-slate-100 transition-colors">
                                      <span className="text-xs text-slate-700 font-medium">
                                        {formatDateUTC(record.hire_date)}
                                      </span>
                                    </td>
                                    <td 
                                      className={`px-3 py-2 text-xs font-bold text-center min-w-[80px] border-r-2 border-white cursor-pointer hover:opacity-80 transition-opacity ${
                                        editingMonth === record.id ? '' : getMonthColor(record.month)
                                      }`}
                                      onClick={(e) => {
                                        if (editingMonth !== record.id) {
                                          e.stopPropagation();
                                          setEditingMonth(record.id);
                                        }
                                      }}
                                    >
                                      {editingMonth === record.id ? (
                                        <select
                                          value={record.month}
                                          onChange={(e) => {
                                            handleUpdateMonth(record.id, e.target.value);
                                          }}
                                          onBlur={() => setEditingMonth(null)}
                                          className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                                          autoFocus
                                        >
                                          {MONTHS.map((m) => (
                                            <option key={m.value} value={m.value}>
                                              {m.value}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        record.month
                                      )}
                                    </td>
                                    <td className="px-3 py-2 min-w-[60px] cursor-pointer hover:bg-slate-100 transition-colors">
                                      <span className="text-xs text-slate-700 font-medium">{record.number_of_days}</span>
                                    </td>
                                    <td 
                                      onClick={() => handleToggleDriverReturn(record)}
                                      className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[80px] border-r-2 border-white ${
                                        record.driver_return 
                                          ? 'bg-emerald-500 text-white' 
                                          : 'bg-red-500 text-white'
                                      }`}
                                    >
                                      {record.driver_return ? 'YES' : 'NO'}
                                    </td>
                                    <td className="px-3 py-2 min-w-[100px] cursor-pointer hover:bg-slate-100 transition-colors">
                                      <span className="text-xs text-slate-700 font-medium">
                                        {formatDateUTC(record.return_date)}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-slate-600">
                                      {editingNote === record.id ? (
                                        <div className="flex gap-2">
                                          <input
                                            type="text"
                                            value={editingNoteValue}
                                            onChange={(e) => setEditingNoteValue(e.target.value)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                handleUpdateNote(record.id, editingNoteValue);
                                              } else if (e.key === 'Escape') {
                                                setEditingNote(null);
                                                setEditingNoteValue('');
                                              }
                                            }}
                                            className="px-2 py-1 border border-slate-300 rounded text-xs w-full"
                                            autoFocus
                                          />
                                          <button
                                            onClick={() => handleUpdateNote(record.id, editingNoteValue)}
                                            className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                          >
                                            Save
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingNote(null);
                                              setEditingNoteValue('');
                                            }}
                                            className="px-2 py-1 bg-slate-300 text-slate-700 rounded text-xs hover:bg-slate-400"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      ) : (
                                        <span
                                          onClick={() => startEditingNote(record)}
                                          className="cursor-pointer hover:text-blue-600 block truncate max-w-xs"
                                          title={record.note || 'Click to add note'}
                                        >
                                          {record.note || 'Click to add note'}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="flex gap-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openEditModal(record);
                                          }}
                                          className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                          title="Edit"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setRecordToDelete(record);
                                            setShowDeleteModal(true);
                                          }}
                                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            
                            {/* Monthly Totals for this driver */}
                            <div className="mt-4 pt-4 border-t border-slate-300">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-slate-900 text-sm">Monthly Breakdown:</span>
                                <div className="flex flex-wrap gap-2">
                                  {MONTHS.map((month) => {
                                    const daysForMonth = records
                                      .filter(r => r.month === month.value)
                                      .reduce((sum, r) => sum + r.number_of_days, 0);
                                    
                                    if (daysForMonth === 0) return null;
                                    
                                    return (
                                      <div key={month.value} className={`px-3 py-1 rounded-full text-xs font-medium ${getMonthColor(month.value)}`}>
                                        {month.value}: {daysForMonth} days
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Day Off Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-slate-900">Add Day Off</h2>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Driver *
                </label>
                <input
                  type="text"
                  value={driverSearch}
                  onChange={(e) => {
                    setDriverSearch(e.target.value);
                    setShowDriverDropdown(true);
                    if (!e.target.value) {
                      setSelectedDriver('');
                    }
                  }}
                  onFocus={() => setShowDriverDropdown(true)}
                  placeholder="Search for a driver..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {showDriverDropdown && filteredDrivers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredDrivers.map((driver) => (
                      <div
                        key={driver.id}
                        onClick={() => handleDriverSelect(driver)}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm text-slate-700"
                      >
                        {driver.first_name} {driver.last_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    From Date *
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    To Date *
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Month *
                  </label>
                  <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select month</option>
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Days (Auto-calculated)
                  </label>
                  <input
                    type="number"
                    value={numberOfDays}
                    readOnly
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Driver Return
                </label>
                <select
                  value={driverReturn ? 'yes' : 'no'}
                  onChange={(e) => setDriverReturn(e.target.value === 'yes')}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {driverReturn && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Return Date
                  </label>
                  <input
                    type="date"
                    value={returnDate}
                    onChange={(e) => setReturnDate(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Note
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any additional notes..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDayOff}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Day Off
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Date Modal */}
      {showReturnDateModal && selectedRecordForReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Set Return Date</h2>
              <p className="text-sm text-slate-600 mt-1">
                Driver: {selectedRecordForReturn.driver_name}
              </p>
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs font-medium text-blue-900">Vacation Period:</p>
                <p className="text-sm text-blue-700 mt-1">
                  {formatDateUTC(selectedRecordForReturn.start_date)} - {formatDateUTC(selectedRecordForReturn.end_date)}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  ({selectedRecordForReturn.number_of_days} days)
                </p>
              </div>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Return Date *
              </label>
              <input
                type="date"
                value={tempReturnDate}
                onChange={(e) => setTempReturnDate(e.target.value)}
                min={selectedRecordForReturn.start_date}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Cannot select a date before {formatDateUTC(selectedRecordForReturn.start_date)}
              </p>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReturnDateModal(false);
                  setSelectedRecordForReturn(null);
                  setTempReturnDate('');
                }}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReturnDate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && recordToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Delete Vacation Record</h2>
              <p className="text-sm text-slate-600 mt-2">
                Are you sure you want to delete this vacation record for {recordToDelete.driver_name}?
              </p>
              <p className="text-sm text-slate-600 mt-1">
                {formatDateUTC(recordToDelete.start_date)} - {formatDateUTC(recordToDelete.end_date)} ({recordToDelete.number_of_days} days)
              </p>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setRecordToDelete(null);
                }}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRecord}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && recordToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Edit Vacation Record</h2>
              <p className="text-sm text-slate-600 mt-1">{recordToEdit.driver_name}</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    From Date *
                  </label>
                  <input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    To Date *
                  </label>
                  <input
                    type="date"
                    value={editEndDate}
                    onChange={(e) => setEditEndDate(e.target.value)}
                    min={editStartDate}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Month *
                  </label>
                  <select
                    value={editMonth}
                    onChange={(e) => setEditMonth(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select month</option>
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Days (Auto-calculated)
                  </label>
                  <input
                    type="number"
                    value={calculateDays(editStartDate, editEndDate)}
                    readOnly
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Driver Return
                </label>
                <select
                  value={editDriverReturn ? 'yes' : 'no'}
                  onChange={(e) => setEditDriverReturn(e.target.value === 'yes')}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {editDriverReturn && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Return Date
                  </label>
                  <input
                    type="date"
                    value={editReturnDate}
                    onChange={(e) => setEditReturnDate(e.target.value)}
                    min={editStartDate}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Note
                </label>
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any additional notes..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setRecordToEdit(null);
                }}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditRecord}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Driver Confirmation Modal */}
      {showDeleteDriverModal && driverToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Delete All Records for Driver</h2>
              <p className="text-sm text-slate-600 mt-2">
                Are you sure you want to delete all vacation records for <strong>{driverToDelete}</strong>?
              </p>
              <p className="text-sm text-red-600 mt-2 font-semibold">
                This action cannot be undone!
              </p>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteDriverModal(false);
                  setDriverToDelete(null);
                }}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDriver}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete All Records
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
